import {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
  memo,
  startTransition,
} from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BarChart,
  type BarChartEventProps,
} from "@/components/ui/tremor-charts/BarChart";
import { getDataGraph } from "../apiService";
import { getAvailableIntervals } from "@/utils/dateUtils";
import axios from "axios";
import { DataGraphResponse, Creative, CreativeReportAd } from "../types";
import ReportLoader from "./ReportLoader";
import { useGlobalStore } from "@/GlobalStore";
import { toast } from "sonner";
import ToastContent from "@/components/common/toast/ToastContent";
import { LuCircleCheck } from "react-icons/lu";
import { ColumnOption, SectionConfigWithMetadata } from "@/types";
import {
  SqSelect,
  SqSelectContent,
  SqSelectItem,
  SqSelectTrigger,
} from "@/components/squircle-ui/select";
import Metrics from "@/components/common/metrics";
import { cache } from "@/services/cache";
import { SqButton } from "@/components/squircle-ui/button";
import { IconDatabase } from "@tabler/icons-react";
import {
  SqTooltip,
  SqTooltipContent,
  SqTooltipProvider,
  SqTooltipTrigger,
} from "@/components/squircle-ui/tooltip";
import { RiArrowDownSLine, RiPercentFill } from "@remixicon/react";
import { CreativeShareTooltip } from "./CreativeShareTooltip";
import { FooterInfo } from "./FooterInfo";
import { AvailableChartColors, AvailableChartColorsKeys } from "@/lib/chartUtils";
import CreativeGalleryPreview from "./preview/CreativeGalleryPreview";
import type { WorkerOutput } from "./CreativeShareChart.worker.d";
import { AGGREGATED_OTHERS_KEY } from "./CreativeShareChart.constants";

// Memoized chart component to prevent unnecessary re-renders
const ChartRenderer = memo(
  function ChartRenderer({
    data,
    categories,
    valueFormatter,
    colors,
    onBarClick,
    graphData,
    selectedMetric,
    processedTotalData,
    primaryKeyToCreativeMap,
    allKeys,
    selectedInterval,
  }: {
    data: Array<{ [key: string]: string | number }>;
    categories: string[];
    valueFormatter: (value: number) => string;
    colors: AvailableChartColorsKeys[];
    onBarClick: (event: BarChartEventProps) => void;
    graphData: DataGraphResponse | null;
    selectedMetric: string | null;
    processedTotalData: Array<{ [key: string]: string | number }>;
    primaryKeyToCreativeMap?: Record<string, CreativeReportAd>;
    allKeys: string[];
    selectedInterval: "daily" | "weekly" | "monthly";
  }) {
    if (data.length === 0) return null;
    return (
      <BarChart
        className="h-full w-full"
        data={data}
        index="x"
        categories={categories}
        valueFormatter={valueFormatter}
        showXAxis={true}
        showYAxis={false}
        chartMargin={{
          right: 0,
          top: 0,
        }}
        showGridLines={false}
        showTooltip={true}
        showLegend={false}
        type="stacked"
        sharedTooltip={false}
        colors={colors}
        onValueChange={onBarClick}
        minValue={0}
        maxValue={100}
        customTooltip={(props) => (
          <CreativeShareTooltip
            {...props}
            valueFormatter={valueFormatter}
            graphData={graphData}
            selectedMetric={selectedMetric}
            processedTotalData={processedTotalData}
            primaryKeyToCreativeMap={primaryKeyToCreativeMap}
            chartColors={colors}
            allKeys={allKeys}
            selectedInterval={selectedInterval}
          />
        )}
      />
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison to prevent re-renders when only gallery state changes
    // Compare primitive values directly, arrays/objects by reference (they're memoized)
    if (
      prevProps.selectedMetric !== nextProps.selectedMetric ||
      prevProps.selectedInterval !== nextProps.selectedInterval ||
      prevProps.graphData !== nextProps.graphData ||
      prevProps.primaryKeyToCreativeMap !== nextProps.primaryKeyToCreativeMap
    ) {
      return false;
    }

    // For arrays, check length first, then reference (they're memoized so reference should work)
    if (
      prevProps.data !== nextProps.data ||
      prevProps.categories !== nextProps.categories ||
      prevProps.colors !== nextProps.colors ||
      prevProps.processedTotalData !== nextProps.processedTotalData ||
      prevProps.allKeys !== nextProps.allKeys
    ) {
      return false;
    }

    // All props are equal, skip re-render
    return true;
  },
);

interface CreativeShareChartProps {
  accountId: string | undefined;
  sectionId: string;
  availableMetrics: ColumnOption[];
  headline: string;
  initialConfig?: {
    interval: "daily" | "weekly" | "monthly";
    metric?: string | null; // Deprecated: use metrics instead
    metrics?: string[]; // Array of metric keys (only first one used)
  };
  onConfigChange?: (config: {
    interval: "daily" | "weekly" | "monthly";
    metric?: string | null;
    metrics?: string[];
  }) => void;
  configWithReportMetadata?: SectionConfigWithMetadata;
  primaryKeyToCreativeMap?: Record<string, CreativeReportAd>;
}

export const CreativeShareChart = memo(function CreativeShareChart({
  accountId,
  sectionId,
  availableMetrics,
  initialConfig,
  onConfigChange,
  configWithReportMetadata,
  primaryKeyToCreativeMap,
}: CreativeShareChartProps) {
  const selectedAccount = useGlobalStore((state) => state.selectedAccount);
  const startDate = useMemo(
    () => new Date(configWithReportMetadata?.date?.start_date || ""),
    [configWithReportMetadata?.date?.start_date],
  );
  const endDate = useMemo(
    () => new Date(configWithReportMetadata?.date?.end_date || ""),
    [configWithReportMetadata?.date?.end_date],
  );

  // Calculate date range in days
  const dateRangeInDays = useMemo(() => {
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }, [startDate, endDate]);

  // Get base available intervals and filter based on date range
  // Rules:
  // - Daily: Available only for date ranges <= 30 days
  // - Weekly: Available only for date ranges <= 180 days (6 months)
  // - Monthly: Always available (no restriction)
  const availableIntervals = useMemo(() => {
    const baseIntervals = getAvailableIntervals(startDate, endDate);

    // Disable Daily after 30 days
    if (dateRangeInDays > 30) {
      baseIntervals.daily = false;
    }

    // Disable Weekly after 6 months (180 days)
    if (dateRangeInDays > 180) {
      baseIntervals.weekly = false;
    }

    return baseIntervals;
  }, [startDate, endDate, dateRangeInDays]);

  const [selectedMetric, setSelectedMetric] = useState<string | null>(() => {
    // Support new multi-metric format first (use first metric)
    if (initialConfig?.metrics && initialConfig.metrics.length > 0) {
      const firstMetric = initialConfig.metrics[0];
      if (availableMetrics.some((metric) => metric.key === firstMetric)) {
        return firstMetric;
      }
    }
    // Fallback to old single metric format for backward compatibility
    if (
      initialConfig?.metric &&
      availableMetrics.some((metric) => metric.key === initialConfig.metric)
    ) {
      return initialConfig.metric;
    }
    // Fall back to first available metric
    return availableMetrics[0]?.key;
  });

  // Separate display state for immediate UI updates (dropdown trigger)
  const [displayMetric, setDisplayMetric] = useState<string | null>(selectedMetric);

  const [selectedInterval, setSelectedInterval] = useState<
    "daily" | "weekly" | "monthly"
  >(() => {
    const baseIntervals = getAvailableIntervals(startDate, endDate);
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Apply date range restrictions
    const intervals = { ...baseIntervals };
    if (diffDays > 30) intervals.daily = false;
    if (diffDays > 180) intervals.weekly = false;

    // Check if initial interval is available
    if (initialConfig?.interval && intervals[initialConfig.interval]) {
      return initialConfig.interval;
    }

    // Determine ideal interval based on date range
    if (diffDays <= 30 && intervals.daily) {
      return "daily";
    } else if (diffDays <= 180 && intervals.weekly) {
      return "weekly";
    } else if (intervals.monthly) {
      return "monthly";
    } else if (intervals.weekly) {
      return "weekly";
    }
    return "daily";
  });

  // Separate display state for immediate UI updates (dropdown trigger)
  const [displayInterval, setDisplayInterval] = useState<"daily" | "weekly" | "monthly">(
    selectedInterval,
  );

  const [graphData, setGraphData] = useState<DataGraphResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [processingData, setProcessingData] = useState(false);

  // Processed data states (will be set by worker)
  const [processedChartData, setProcessedChartData] = useState<
    Array<{ [key: string]: string | number }>
  >([]);
  const [processedChartTotalsData, setProcessedChartTotalsData] = useState<
    Array<{ [key: string]: string | number }>
  >([]);
  const [allKeys, setAllKeys] = useState<string[]>([]);

  const [openGalleryView, setOpenGalleryView] = useState(false);
  const [galleryMediaItems, setGalleryMediaItems] = useState<
    Array<
      Creative & {
        type: "image" | "video" | "mixed";
        ad_name: string;
        ad_id?: string;
        creative_id: string;
      }
    >
  >([]);

  const FOOTER_DISMISSED_KEY = "creative-share-chart-footer-dismissed";
  const COMPARE_DATE_WARNING_KEY = "creative-share-chart-compare-date-warning-dismissed";

  // Check if compare date is enabled
  const isCompareDateEnabled = useMemo(() => {
    return !!(
      configWithReportMetadata?.compare_date?.start_date &&
      configWithReportMetadata?.compare_date?.end_date
    );
  }, [configWithReportMetadata?.compare_date]);

  // Use a ref to track the last emitted configuration
  const lastEmittedConfig = useRef<{
    interval: "daily" | "weekly" | "monthly";
    metric?: string | null;
    metrics?: string[];
  } | null>(initialConfig ? { ...initialConfig } : null);

  // Use ref for timeout to avoid state updates and stale closures
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMounted = useRef(true);
  const prevAvailableMetricsLength = useRef<number>(availableMetrics.length);
  const workerRef = useRef<Worker | null>(null);
  const prevConfigRef = useRef<SectionConfigWithMetadata | null>(null);
  const cancelTokenSourceRef = useRef<ReturnType<typeof axios.CancelToken.source> | null>(
    null,
  );

  // Initialize Web Worker
  useEffect(() => {
    // Create worker
    workerRef.current = new Worker(
      new URL("./CreativeShareChart.worker.ts", import.meta.url),
      { type: "module" },
    );

    // Listen for messages from worker
    workerRef.current.onmessage = (event: MessageEvent<WorkerOutput>) => {
      if (event.data.type === "PROCESSING_COMPLETE") {
        const { processedChartData, processedChartTotalsData, allKeys } =
          event.data.payload;

        // Update state with processed data
        startTransition(() => {
          setProcessedChartData(processedChartData);
          setProcessedChartTotalsData(processedChartTotalsData);
          setAllKeys(allKeys);
          setProcessingData(false);
        });
      }
    };

    workerRef.current.onerror = (error) => {
      console.error("Worker error:", error);
      setProcessingData(false);
    };

    return () => {
      // Terminate worker on unmount
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, []);

  // Cleanup effect - runs once on mount/unmount
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      // Clear polling timeout
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      // Clear large data references on unmount
      setGraphData(null);
      setGalleryMediaItems([]);
    };
  }, []);

  // Clear polling timeout helper - stable reference since it uses ref
  const clearPollingTimeout = useCallback(() => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  }, []);

  // Update configuration when selections change
  useEffect(() => {
    const newConfig = {
      interval: selectedInterval,
      metrics: selectedMetric ? [selectedMetric] : [],
      metric: selectedMetric, // Keep for backward compatibility
    };

    // Only emit if the configuration has actually changed
    const lastMetric =
      lastEmittedConfig.current?.metrics && lastEmittedConfig.current.metrics.length > 0
        ? lastEmittedConfig.current.metrics[0]
        : lastEmittedConfig.current?.metric;
    if (
      !lastEmittedConfig.current ||
      lastEmittedConfig.current.interval !== newConfig.interval ||
      lastMetric !== selectedMetric
    ) {
      lastEmittedConfig.current = newConfig;
      onConfigChange?.(newConfig);
    }
  }, [selectedInterval, selectedMetric, onConfigChange]);

  // Update state if initialConfig changes significantly
  useEffect(() => {
    if (initialConfig) {
      // Get the metric to compare (from metrics array or single metric)
      const currentMetric =
        initialConfig.metrics && initialConfig.metrics.length > 0
          ? initialConfig.metrics[0]
          : initialConfig.metric;
      const lastMetric =
        lastEmittedConfig.current?.metrics && lastEmittedConfig.current.metrics.length > 0
          ? lastEmittedConfig.current.metrics[0]
          : lastEmittedConfig.current?.metric;

      const configChanged =
        lastEmittedConfig.current?.interval !== initialConfig.interval ||
        lastMetric !== currentMetric;

      if (configChanged) {
        // Validate interval and metric before updating
        if (availableIntervals[initialConfig.interval]) {
          setSelectedInterval(initialConfig.interval);
          setDisplayInterval(initialConfig.interval);
        }
        if (
          currentMetric &&
          availableMetrics.some((metric) => metric.key === currentMetric)
        ) {
          setSelectedMetric(currentMetric);
          setDisplayMetric(currentMetric);
        }
        lastEmittedConfig.current = initialConfig;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConfig]);

  // Auto-select ideal interval when current one becomes unavailable (before API request)
  // This ensures the correct interval is selected before fetching data
  useEffect(() => {
    // Only switch if current interval is disabled
    if (!availableIntervals[selectedInterval]) {
      // Determine ideal interval based on date range
      // Priority: daily (if <= 30 days) > weekly (if <= 180 days) > monthly
      let idealInterval: "daily" | "weekly" | "monthly";

      if (dateRangeInDays <= 30 && availableIntervals.daily) {
        idealInterval = "daily";
      } else if (dateRangeInDays <= 180 && availableIntervals.weekly) {
        idealInterval = "weekly";
      } else if (availableIntervals.monthly) {
        idealInterval = "monthly";
      } else if (availableIntervals.weekly) {
        idealInterval = "weekly";
      } else {
        idealInterval = "daily";
      }

      setSelectedInterval(idealInterval);
      setDisplayInterval(idealInterval);
    }
  }, [dateRangeInDays, availableIntervals, selectedInterval]);

  // Sync display states with actual states when they change
  useEffect(() => {
    setDisplayMetric(selectedMetric);
  }, [selectedMetric]);

  useEffect(() => {
    setDisplayInterval(selectedInterval);
  }, [selectedInterval]);

  useEffect(() => {
    if (!availableMetrics.some((metric) => metric.key === selectedMetric)) {
      const newMetric = availableMetrics[0];
      if (newMetric) {
        toast.message(
          <ToastContent
            icon={LuCircleCheck}
            headingPrefix="Switched graph metric to"
            headingHighlight={`"${newMetric.label}"`}
            subheadingPrefix={selectedMetric ? "Previous metric has been removed." : ""}
          />,
        );
        setSelectedMetric(newMetric.key);
        setDisplayMetric(newMetric.key);
      } else {
        setSelectedMetric(null);
        setDisplayMetric(null);
        setGraphData(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableMetrics.length]);

  useEffect(() => {
    // Helper function to compare configs excluding metrics and additional_config
    const configsEqualExcludingMetricsAndAdditionalConfig = (
      prev: SectionConfigWithMetadata | null,
      current: SectionConfigWithMetadata | null,
    ): boolean => {
      if (!prev || !current) return prev === current;

      // Create copies without metrics and additional_config for comparison
      const prevWithoutExcluded = {
        ...prev,
        column_choices: prev.column_choices
          ? {
              ...prev.column_choices,
              metric: undefined,
            }
          : undefined,
        additional_config: undefined,
      };
      const currentWithoutExcluded = {
        ...current,
        column_choices: current.column_choices
          ? {
              ...current.column_choices,
              metric: undefined,
            }
          : undefined,
        additional_config: undefined,
      };

      return (
        JSON.stringify(prevWithoutExcluded) === JSON.stringify(currentWithoutExcluded)
      );
    };

    // Check if only metrics or additional_config changed (and availableMetrics.length hasn't changed)
    const availableMetricsLengthChanged =
      prevAvailableMetricsLength.current !== availableMetrics.length;
    const metricsChanged =
      JSON.stringify(prevConfigRef.current?.column_choices?.metric) !==
      JSON.stringify(configWithReportMetadata?.column_choices?.metric);
    const additionalConfigChanged =
      JSON.stringify(prevConfigRef.current?.additional_config) !==
      JSON.stringify(configWithReportMetadata?.additional_config);

    const onlyMetricsOrAdditionalConfigChanged =
      prevConfigRef.current &&
      configWithReportMetadata &&
      configsEqualExcludingMetricsAndAdditionalConfig(
        prevConfigRef.current,
        configWithReportMetadata,
      ) &&
      !availableMetricsLengthChanged &&
      (metricsChanged || additionalConfigChanged);

    // Update refs for next comparison
    prevConfigRef.current = configWithReportMetadata ?? null;
    prevAvailableMetricsLength.current = availableMetrics.length;
    // Skip fetch if only metrics or additional_config changed (but availableMetrics.length is the same)
    if (onlyMetricsOrAdditionalConfigChanged) {
      return () => {
        // No cleanup needed if we didn't fetch
      };
    }

    // Cancel any existing request before starting a new one
    if (cancelTokenSourceRef.current) {
      cancelTokenSourceRef.current.cancel("Request cancelled due to new request");
    }

    // Clear any existing polling timeout before starting a new request
    clearPollingTimeout();

    // Create new cancel token source
    const cancelTokenSource = axios.CancelToken.source();
    cancelTokenSourceRef.current = cancelTokenSource;

    const fetchGraphData = async (isPolling?: boolean) => {
      if (!accountId || sectionId.startsWith("__temp-")) return;

      // Generate cache key
      const cacheKey = cache.generateKey(
        `/creative/${accountId}/data-graph/${sectionId}/`,
        {
          interval: selectedInterval,
          metadata: configWithReportMetadata,
          availableMetrics: availableMetrics.map((metric) => metric.key),
          use_new_breakdown_structure: true,
          data_view: "creative_share_view",
        },
        accountId,
      );
      const metadata = {
        ...configWithReportMetadata,
        column_choices: {
          ...configWithReportMetadata?.column_choices,
          metric: availableMetrics.map((metric) => metric.key),
        },
      };

      // Try to get from cache first (unless polling)
      let hasCachedData = false;
      if (!isPolling) {
        setLoading(true);
        try {
          const cachedData = await cache.get<DataGraphResponse>(cacheKey);
          if (cachedData) {
            // Use startTransition for cached data to keep UI responsive
            startTransition(() => {
              setGraphData(cachedData);
              setLoading(false);
            });
            hasCachedData = true;
          }
        } catch (error) {
          console.warn("Failed to get data from cache:", error);
        }
      }

      try {
        // Always fetch fresh data from API with creative_share_view
        const response = await getDataGraph(
          accountId,
          sectionId,
          selectedInterval,
          metadata || {},
          cancelTokenSource.token,
          true, // use_new_breakdown_structure
          "creative_share_view", // data_view
        );

        // If component unmounted during request, don't proceed
        if (!isMounted.current) return;

        // Cache the response (only if not pending)
        if (response?.status !== "pending") {
          try {
            await cache.set(cacheKey, response, 24 * 60 * 60 * 1000); // 24 hours TTL
          } catch (error) {
            console.warn("Failed to cache data:", error);
          }
        }

        // Check response status and set up polling if needed
        if (response?.status === "pending") {
          // Set the data even while pending
          startTransition(() => {
            setGraphData(response);
          });

          // Clear any existing timeout before setting new one
          clearPollingTimeout();

          // Set up next polling iteration using ref
          pollingTimeoutRef.current = setTimeout(() => {
            void fetchGraphData(true);
          }, 5000);
        } else {
          // Only update UI with fresh data if we didn't have cached data
          // This prevents flicker when cached data is already showing
          if (!hasCachedData) {
            // Use startTransition for non-urgent data updates to keep UI responsive
            startTransition(() => {
              setGraphData(response);
              setLoading(false);
            });
          }
          clearPollingTimeout();
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error("Error fetching graph data:", error);
          setLoading(false);
          clearPollingTimeout();
        }
      }
    };

    void fetchGraphData();
    prevAvailableMetricsLength.current = availableMetrics.length;

    return () => {
      // Cleanup is handled before making new requests
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accountId,
    sectionId,
    selectedInterval,
    availableMetrics.length,
    configWithReportMetadata,
  ]);

  // Memoize the maps for better performance
  const intervalRangesMap = useMemo(
    () =>
      new Map(
        graphData?.data?.interval_ranges?.map((range) => [range.label, range]) || [],
      ),
    [graphData?.data?.interval_ranges],
  );

  // Create value formatter with prefix/suffix from graph data columns
  const selectedMetricColumn = graphData?.data?.columns?.find(
    (column) => column.key === selectedMetric,
  );
  const valueKeyColumn = graphData?.data?.columns?.find(
    (column) => column.key === selectedMetricColumn?.value_key || "",
  );
  const valuePrefix = valueKeyColumn?.prefix || "";
  const valueSuffix = valueKeyColumn?.suffix || "";

  // Process data in Web Worker when data or metric changes
  useEffect(() => {
    if (!graphData?.data?.data_points || !selectedMetric || !workerRef.current) {
      setProcessedChartData([]);
      setProcessedChartTotalsData([]);
      setAllKeys([]);
      setProcessingData(false);
      return;
    }

    // Clear old processed data and set processing state to show loader
    setProcessingData(true);
    setProcessedChartData([]);
    setProcessedChartTotalsData([]);
    setAllKeys([]);

    // Send data to worker for processing
    workerRef.current.postMessage({
      type: "PROCESS_CHART_DATA",
      payload: {
        dataPoints: graphData.data.data_points,
        dataTotals: graphData.data.data_totals || [],
        selectedMetric,
        intervalRangesMap: Array.from(intervalRangesMap.entries()), // Convert Map to array for transfer
        selectedInterval,
        valueKeyColumnKey: valueKeyColumn?.key,
      },
    });
  }, [
    graphData?.data?.data_points,
    graphData?.data?.data_totals,
    selectedMetric,
    intervalRangesMap,
    selectedInterval,
    selectedAccount?.time_zone,
    valueKeyColumn?.key,
  ]);

  // Create colors array for the chart
  // BarChart expects AvailableChartColorsKeys[], so we'll use default colors
  // and cycle through them based on the order of allKeys
  const chartColors = useMemo(() => {
    // Use available colors from chartUtils, cycling through them for each category
    // This ensures we have enough colors for all categories
    const availableColors: AvailableChartColorsKeys[] = AvailableChartColors;

    // Return enough colors for all categories, cycling if needed
    return allKeys.map((_, index) => availableColors[index % availableColors.length]);
  }, [allKeys]);

  const handleMetricChange = useCallback((value: string) => {
    // Update display metric immediately for dropdown trigger (urgent)
    setDisplayMetric(value);
    setProcessingData(true);

    // Defer the actual metric change and chart processing (non-urgent)
    startTransition(() => {
      setSelectedMetric(value);
    });
  }, []);

  // Handler for Metrics component - convert ColumnOption[] to string
  const handleMetricsChange = useCallback(
    (selectedMetrics: ColumnOption[]) => {
      if (selectedMetrics.length > 0) {
        handleMetricChange(selectedMetrics[0].key);
      }
    },
    [handleMetricChange],
  );

  // Handler for selected metrics - convert string to ColumnOption[]
  // Use displayMetric for UI display (what user sees in dropdown)
  const getSelectedMetrics = useMemo(() => {
    const selectedMetricObj = availableMetrics.find((m) => m.key === displayMetric);
    if (!selectedMetricObj) return [];

    return [selectedMetricObj];
  }, [availableMetrics, displayMetric]);

  const valueFormatter = useCallback(
    (value: number) => {
      const formattedValue = value.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });

      // No spaces between prefix/suffix and value to prevent Recharts from splitting into multiple tspan elements
      return valuePrefix && valueSuffix
        ? `${valuePrefix}${formattedValue}${valueSuffix}`
        : valuePrefix
          ? `${valuePrefix}${formattedValue}`
          : valueSuffix
            ? `${formattedValue}${valueSuffix}`
            : formattedValue;
    },
    [valuePrefix, valueSuffix],
  );

  // Handler for bar click to open gallery view
  // Optimize by opening dialog immediately, then loading data
  const handleBarClick = useCallback(
    (event: BarChartEventProps) => {
      if (!event || !event.categoryClicked) {
        // If event is null/undefined, it means the active state was cleared
        // This is expected and we don't need to do anything
        return;
      }

      const creativeName = event.categoryClicked;

      // Don't open gallery for aggregated "Others" category (represents multiple creatives)
      if (creativeName === AGGREGATED_OTHERS_KEY) {
        return;
      }

      const creativeAd = primaryKeyToCreativeMap?.[creativeName];
      if (!creativeAd) {
        return;
      }
      const creative = creativeAd.creative;

      if (creative) {
        // Open dialog immediately for instant feedback
        setOpenGalleryView(true);

        // Prepare gallery item data (lightweight operation)
        const hasImages = creative.images && creative.images.length > 0;
        const hasVideos = creative.videos && creative.videos.length > 0;
        let creativeType: "image" | "video" | "mixed" = "image";
        if (hasImages && hasVideos) {
          creativeType = "mixed";
        } else if (hasVideos) {
          creativeType = "video";
        }

        // Format creative for gallery view
        const galleryItem: Creative & {
          type: "image" | "video" | "mixed";
          ad_name: string;
          ad_id?: string;
          creative_id: string;
        } = {
          ...creative,
          type: creativeType,
          ad_name: creativeAd.ad_name || "",
          ad_id: creativeAd.ad_id || "",
          creative_id: creativeName,
        };

        setGalleryMediaItems([galleryItem]);
      }
    },
    [primaryKeyToCreativeMap],
  );

  return (
    <Card className="mb-2 bg-white rounded-[12px] border-none squircle squircle-radius-[12px] squircle-smooth-[1] squircle-outline-[1px] squircle-outline-color-border-gray shadow-none pt-[13px] p-4 overflow-visible">
      <CardHeader className="p-0 space-y-0 flex flex-row items-center justify-between">
        <div className="flex flex-col">
          <CardTitle>
            <div className="flex items-center gap-2">
              <RiPercentFill size={16} className="text-brand-primary-600 shrink-0" />
              <h4 className="text-brand-primary-800 overflow-hidden text-ellipsis relative top-[1px] text-[13px] font-medium not-italic leading-none">
                Creative share
              </h4>
            </div>
          </CardTitle>
        </div>
        <div className="flex gap-2">
          <SqSelect
            value={displayInterval}
            onValueChange={(value: "weekly" | "monthly") => {
              // Update display interval immediately for dropdown trigger (urgent)
              setDisplayInterval(value);
              setLoading(true);

              // Defer the actual interval change and chart processing (non-urgent)
              startTransition(() => {
                setSelectedInterval(value);
              });
            }}
          >
            <SqSelectTrigger
              radius={6}
              className="h-7 flex items-center gap-[6px] bg-transparent py-1 pl-2.5 pr-2"
            >
              <span className="text-[#67749B] text-ellipsis text-[11px] font-normal leading-[19.157px] tracking-[0.24px]">
                Period
              </span>
              <span className="overflow-hidden [-webkit-box-orient:vertical] [-webkit-line-clamp:1] text-brand-primary-new/80 text-ellipsis text-[11px] font-medium leading-[19.157px] tracking-[0.24px]">
                {displayInterval.charAt(0).toUpperCase() + displayInterval.slice(1)}
              </span>
            </SqSelectTrigger>
            <SqSelectContent radius={12} className="bg-white rounded-[12px]">
              {(["daily", "weekly", "monthly"] as const).map(
                (interval) =>
                  availableIntervals[interval] && (
                    <SqSelectItem
                      key={interval}
                      value={interval}
                      className="squircle-mask overflow-hidden [-webkit-box-orient:vertical] [-webkit-line-clamp:1] text-brand-primary-new/80 text-ellipsis text-[11px] font-normal leading-[19.157px] tracking-[0.24px]"
                    >
                      {interval.charAt(0).toUpperCase() + interval.slice(1)}
                    </SqSelectItem>
                  ),
              )}
            </SqSelectContent>
          </SqSelect>
          <Metrics
            renderingStyle="single_select"
            selectedMetrics={getSelectedMetrics}
            onMetricChange={handleMetricsChange}
            metricsOptions={availableMetrics}
            trigger={
              <SqTooltipProvider>
                <SqTooltip>
                  <SqTooltipTrigger>
                    <SqButton
                      className="flex items-center gap-[6px] h-7 bg-transparent pl-2.5 pr-2 py-1"
                      bgColor="#FFFFFF"
                      hasOutline
                      shadow={"sm"}
                      hoverBgColor="#FBFBFB"
                      radius={6}
                    >
                      <span className="text-[#67749B] text-ellipsis text-[11px] font-normal leading-[19.157px] tracking-[0.24px]">
                        Metric{" "}
                      </span>
                      {getSelectedMetrics?.[0] ? (
                        <div className="flex items-center gap-1 min-w-0 flex-1">
                          {getSelectedMetrics[0]?.sub_type === "EXTERNAL" && (
                            <IconDatabase
                              size={14}
                              className={`text-[#02185999] shrink-0 -translate-y-[0.5px]`}
                            />
                          )}
                          <span className="truncate text-brand-primary-new/80 text-[11px] font-medium leading-[19.157px] tracking-[0.24px] max-w-[160px]">
                            {getSelectedMetrics[0]?.label}
                          </span>
                          {getSelectedMetrics[0]?.show_platforms && (
                            <span className="text-[#FF4500] shrink-0">*</span>
                          )}
                        </div>
                      ) : null}
                      <RiArrowDownSLine size={14} color="#9aa3bd" className="shrink-0" />
                    </SqButton>
                  </SqTooltipTrigger>
                  {getSelectedMetrics[0]?.show_platforms &&
                    getSelectedMetrics[0]?.platforms &&
                    getSelectedMetrics[0]?.platforms.length > 0 && (
                      <SqTooltipContent
                        portal={true}
                        side="bottom"
                        align="start"
                        alignOffset={20}
                      >
                        <p>
                          {getSelectedMetrics[0]?.label}
                          <br />
                          Available for{" "}
                          {getSelectedMetrics[0]?.platforms
                            .map(
                              (platform) =>
                                platform.charAt(0).toUpperCase() +
                                platform.slice(1).toLowerCase(),
                            )
                            .join(", ")}
                        </p>
                      </SqTooltipContent>
                    )}
                </SqTooltip>
              </SqTooltipProvider>
            }
          />
        </div>
      </CardHeader>
      <CardContent className="p-0 mt-[24px]">
        <div className="h-[320px] w-full [&_rect[opacity='0.3']]:!opacity-100">
          {loading ? (
            <ReportLoader message={graphData?.message} showProgressBar={true} />
          ) : processingData ? (
            <ReportLoader message="Processing chart data..." showProgressBar={true} />
          ) : processedChartData.length > 0 ? (
            <ChartRenderer
              data={processedChartData}
              categories={allKeys}
              valueFormatter={valueFormatter}
              colors={chartColors}
              onBarClick={handleBarClick}
              graphData={graphData}
              selectedMetric={selectedMetric}
              processedTotalData={processedChartTotalsData}
              primaryKeyToCreativeMap={primaryKeyToCreativeMap}
              allKeys={allKeys}
              selectedInterval={selectedInterval}
            />
          ) : graphData?.status === "pending" ? (
            <ReportLoader message={graphData?.message} showProgressBar={true} />
          ) : null}
        </div>
      </CardContent>
      {isCompareDateEnabled ? (
        <FooterInfo
          description="Compare dates isn't supported for Creative Share."
          backgroundColor="#FFF7EB"
          textColor="#FF9D00"
          iconBgColor="white"
          iconColor="#FF9D00"
          buttonText="Okay, got it"
          buttonIconColor="#FF9D00"
          showButton={false}
          storageKey={COMPARE_DATE_WARNING_KEY}
        />
      ) : (
        <FooterInfo
          description="Volume Stack uses data from all pages to stay accurate."
          backgroundColor="#E7F3FF"
          textColor="#0071D8"
          iconBgColor="white"
          iconColor="#2A94F4"
          borderColor="#C6E3FF"
          buttonText="Okay, got it"
          buttonIconColor="#FF6A33"
          showButton={true}
          storageKey={FOOTER_DISMISSED_KEY}
        />
      )}

      <CreativeGalleryPreview
        open={openGalleryView}
        onOpenChange={(open) => {
          setOpenGalleryView(open);
          // Clear gallery items when closing to free memory
          if (!open) {
            setGalleryMediaItems([]);
          }
        }}
        creatives={galleryMediaItems}
        accountId={accountId}
        enableSearch={false}
      />
    </Card>
  );
});
